import { useEffect, useState } from "react";
import { Link, useLocation, useParams, Navigate, useOutlet } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useSpring } from "framer-motion";
import { LanguageProvider, useLang } from "@/context/LanguageContext";
import { NAV_ITEMS, ARTIST_NAME, CONTACT, SOCIALS } from "@/data/content";
import { Cursor } from "@/components/Cursor";
import { IntroLoader } from "@/components/IntroLoader";
import { Magnetic, EASE } from "@/components/Shared";
import { Seo } from "@/components/Seo";
import { ConsentProvider, ConsentBanner, useConsent } from "@/components/Consent";
import { ArrowUp } from "lucide-react";
import { useRedX } from "@/components/RedX";

/* ---------------- Header ---------------- */
const Header = ({ onMenu }) => {
  const { lang, t, lp, switchTo } = useLang();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return (
    <header
      className={`fixed left-0 top-0 z-[60] w-full transition-all duration-500 ${
        scrolled ? "border-b border-white/5 bg-[#0B0B0C]/75 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-6 py-5 sm:px-12 lg:px-20">
        <Link
          to={lp("")}
          data-testid="nav-logo"
          className="font-serif text-lg font-medium tracking-[0.18em] text-[#F9F8F6] transition-colors duration-300 hover:text-[#C5A059] sm:text-xl"
        >
          {ARTIST_NAME}
        </Link>
        <div className="flex items-center gap-5 sm:gap-8">
          <div className="flex items-center gap-2 font-sans text-[11px] font-medium tracking-[0.25em]">
            <button
              data-testid="language-switch-de-button"
              onClick={() => switchTo("de")}
              className={`transition-colors duration-300 ${lang === "de" ? "text-[#C5A059]" : "text-[#71717A] hover:text-[#F9F8F6]"}`}
            >
              DE
            </button>
            <span className="text-[#3a3a3d]">|</span>
            <button
              data-testid="language-switch-en-button"
              onClick={() => switchTo("en")}
              className={`transition-colors duration-300 ${lang === "en" ? "text-[#C5A059]" : "text-[#71717A] hover:text-[#F9F8F6]"}`}
            >
              EN
            </button>
          </div>
          <Link
            to={lp("/contact")}
            className="hidden font-sans text-[11px] font-medium uppercase tracking-[0.3em] text-[#F9F8F6] transition-colors duration-300 hover:text-[#C5A059] md:block"
          >
            {t.nav.contact}
          </Link>
          <Magnetic strength={0.4}>
            <button
              data-testid="nav-menu-toggle-button"
              onClick={onMenu}
              className="group flex items-center gap-3"
              aria-label={t.nav.menu}
              aria-haspopup="dialog"
            >
              <span className="hidden font-sans text-[11px] font-medium tracking-[0.3em] text-[#A1A1AA] transition-colors group-hover:text-[#C5A059] sm:block">
                {t.nav.menu}
              </span>
              <span className="flex flex-col gap-1.5">
                <span className="h-px w-7 bg-[#F9F8F6] transition-all duration-300 group-hover:w-5 group-hover:bg-[#C5A059]" />
                <span className="h-px w-5 bg-[#F9F8F6] transition-all duration-300 group-hover:w-7 group-hover:bg-[#C5A059]" />
              </span>
            </button>
          </Magnetic>
        </div>
      </div>
    </header>
  );
};

/* ---------------- Fullscreen Menu ---------------- */
const FullscreenMenu = ({ open, onClose }) => {
  const { t, lp } = useLang();
  const [preview, setPreview] = useState(NAV_ITEMS[0].img);
  const [activeKey, setActiveKey] = useState("home");
  const location = useLocation();

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="fullscreen-menu"
          initial={{ clipPath: "inset(0 0 100% 0)" }}
          animate={{ clipPath: "inset(0 0 0% 0)" }}
          exit={{ clipPath: "inset(100% 0 0 0)" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="fixed inset-0 z-[70] flex bg-[#0B0B0C]"
        >
          {/* faint gold ambient */}
          <div className="pointer-events-none absolute -left-40 top-1/3 h-[60vh] w-[60vh] rounded-full bg-[#C5A059]/[0.04] blur-[120px]" />

          <div className="absolute right-6 top-6 z-10 sm:right-12">
            <Magnetic strength={0.4}>
              <button
                data-testid="menu-close-button"
                onClick={onClose}
                className="font-sans text-[11px] font-medium tracking-[0.3em] text-[#A1A1AA] transition-colors hover:text-[#C5A059]"
              >
                {t.nav.close} ✕
              </button>
            </Magnetic>
          </div>

          {/* Links */}
          <div className="w-full overflow-y-auto px-6 sm:px-12 lg:w-[58%] lg:px-20">
            <div className="mx-auto flex min-h-full w-full flex-col justify-center py-24">
            {NAV_ITEMS.map((item, i) => (
              <motion.div
                key={item.key}
                initial={{ opacity: 0, y: 44 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18, transition: { duration: 0.25, delay: 0 } }}
                transition={{ delay: 0.25 + i * 0.07, duration: 0.7, ease: EASE }}
              >
                <div
                  className="group border-b border-white/5"
                  onMouseEnter={() => {
                    setPreview(item.img);
                    setActiveKey(item.key);
                  }}
                >
                  <Link
                    to={lp(item.path)}
                    data-testid={`menu-link-${item.key}`}
                    className="flex items-baseline gap-5 py-2.5 sm:py-3"
                  >
                    <span className="font-mono text-[10px] tracking-[0.3em] text-[#C5A059]/50">{item.no}</span>
                    <span
                      className={`font-serif text-[9vw] font-light uppercase leading-[1.05] tracking-wide transition-all duration-500 group-hover:translate-x-4 sm:text-5xl lg:text-6xl xl:text-7xl ${
                        activeKey === item.key ? "text-[#C5A059]" : "text-[#F9F8F6] group-hover:text-[#C5A059]"
                      }`}
                    >
                      {t.nav[item.key]}
                    </span>
                  </Link>
                  {item.children && (
                    <div className="flex max-h-16 flex-wrap gap-x-7 gap-y-2 overflow-hidden pb-4 pl-12 transition-all duration-700 lg:max-h-0 lg:pb-0 lg:opacity-0 lg:group-hover:max-h-16 lg:group-hover:pb-4 lg:group-hover:opacity-100">
                      {item.children.map((c, ci) => (
                        <motion.span key={c.key} transition={{ delay: ci * 0.05 }}>
                          <Link
                            to={lp(c.path)}
                            data-testid={`menu-link-${c.key}`}
                            className="font-sans text-[11px] uppercase tracking-[0.25em] text-[#71717A] transition-colors duration-300 hover:text-[#C5A059]"
                          >
                            — {t.nav[c.key]}
                          </Link>
                        </motion.span>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 pl-1"
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">{CONTACT.email}</span>
              {SOCIALS.map((s) => (
                <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`menu-social-${s.label.toLowerCase()}`} className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#71717A] transition-colors hover:text-[#C5A059]">
                  {s.label} ↗
                </a>
              ))}
            </motion.div>
            </div>
          </div>

          {/* Preview image */}
          <div className="relative hidden w-[42%] overflow-hidden lg:block">
            <AnimatePresence mode="wait">
              <motion.div key={preview} className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}>
                <motion.img
                  src={preview}
                  alt=""
                  initial={{ scale: 1.15 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 1.4, ease: EASE }}
                  className="h-full w-full object-cover opacity-70"
                />
              </motion.div>
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0C] via-[#0B0B0C]/30 to-transparent" />
            <div className="absolute bottom-10 left-10">
              <AnimatePresence mode="wait">
                <motion.span
                  key={activeKey}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#C5A059]"
                >
                  {t.nav[activeKey]}
                </motion.span>
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ---------------- Footer ---------------- */
const Footer = () => {
  const { t, lp, lang, switchTo } = useLang();
  const consent = useConsent();
  return (
    <footer data-testid="site-footer" className="relative overflow-hidden border-t border-white/5 bg-[#0B0B0C]">
      <div className="pointer-events-none select-none overflow-hidden pt-10">
        <motion.p
          initial={{ y: "40%", opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: EASE }}
          className="whitespace-nowrap text-center font-serif text-[13vw] font-light leading-[0.85] text-outline"
        >
          {ARTIST_NAME}
        </motion.p>
      </div>
      <div className="px-6 py-16 sm:px-12 lg:px-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="font-serif text-2xl font-light tracking-wide text-[#F9F8F6]">{ARTIST_NAME}</p>
            <p className="mt-3 font-sans text-xs uppercase tracking-[0.25em] text-[#71717A]">{t.footer.tagline}</p>
            <div className="mt-8 flex items-center gap-2 font-sans text-[11px] tracking-[0.25em]">
              <button
                data-testid="footer-language-de-button"
                onClick={() => switchTo("de")}
                className={lang === "de" ? "text-[#C5A059]" : "text-[#71717A] hover:text-[#F9F8F6]"}
              >
                DE
              </button>
              <span className="text-[#3a3a3d]">|</span>
              <button
                data-testid="footer-language-en-button"
                onClick={() => switchTo("en")}
                className={lang === "en" ? "text-[#C5A059]" : "text-[#71717A] hover:text-[#F9F8F6]"}
              >
                EN
              </button>
            </div>
          </div>
          <div className="lg:col-span-3">
            <p className="mb-5 font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">{t.footer.explore}</p>
            <ul className="space-y-3">
              {NAV_ITEMS.map((item) => (
                <li key={item.key}>
                  <Link to={lp(item.path)} className="group inline-flex items-center gap-2 font-sans text-sm text-[#A1A1AA] transition-colors duration-300 hover:text-[#C5A059]">
                    <span className="h-px w-0 bg-[#C5A059] transition-all duration-400 group-hover:w-4" />
                    {t.nav[item.key]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-2">
            <p className="mb-5 font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">{t.footer.contact}</p>
            <ul className="space-y-3 font-sans text-sm text-[#A1A1AA]">
              <li data-testid="footer-email">{CONTACT.email}</li>
              <li><a href={CONTACT.phoneHref} data-testid="footer-phone" className="transition-colors hover:text-[#C5A059]">{CONTACT.phone}</a></li>
              <li className="text-[#71717A]">{CONTACT.street} · {CONTACT.city}</li>
            </ul>
          </div>
          <div className="lg:col-span-2">
            <p className="mb-5 font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">{t.footer.social}</p>
            <ul className="space-y-3 font-sans text-sm">
              {SOCIALS.map((s) => (
                <li key={s.label}>
                  <a href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`footer-social-${s.label.toLowerCase()}`} className="text-[#A1A1AA] transition-colors duration-300 hover:text-[#C5A059]">
                    {s.label} ↗
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-white/5 pt-8 sm:flex-row sm:items-center">
          <p className="font-mono text-[10px] tracking-[0.2em] text-[#71717A]">
            © {new Date().getFullYear()} {ARTIST_NAME}. {t.footer.rights}
          </p>
          <div className="flex gap-6 font-sans text-[11px] uppercase tracking-[0.2em] text-[#71717A]">
            <Link to={lp("/impressum")} data-testid="footer-imprint-link" className="transition-colors hover:text-[#C5A059]">{t.footer.imprint}</Link>
            <Link to={lp("/datenschutz")} data-testid="footer-privacy-link" className="transition-colors hover:text-[#C5A059]">{t.footer.privacy}</Link>
            <button type="button" onClick={consent.openSettings} data-testid="footer-cookie-settings-button" className="uppercase transition-colors hover:text-[#C5A059]">{t.footer.cookies}</button>
          </div>
        </div>
      </div>
    </footer>
  );
};

/* ---------------- Scroll progress ---------------- */
const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 26, mass: 0.3 });
  return (
    <motion.div
      data-testid="scroll-progress-bar"
      className="fixed left-0 top-0 z-[65] h-[2px] w-full origin-left bg-[#C5A059]/80"
      style={{ scaleX }}
    />
  );
};

/* ---------------- Page transition: curtain cover → title → reveal ---------------- */
const PageTransition = () => {
  const location = useLocation();
  const { t } = useLang();
  const outlet = useOutlet();
  const rest = location.pathname.replace(/^\/(de|en)/, "");
  const allItems = NAV_ITEMS.flatMap((n) => [n, ...(n.children || [])]);
  const labelKey = (allItems.find((n) => n.path === rest) || allItems[0]).key;
  return (
    <>
      <IntroLoader />
      <AnimatePresence mode="wait">
        <motion.div
          key={`curtain-${location.pathname}`}
          data-testid="page-transition-curtain"
          className="pointer-events-none fixed inset-0 z-[85] flex items-center justify-center bg-[#0B0B0C]"
          initial={{ scaleY: 1, transformOrigin: "top" }}
          animate={{ scaleY: 0, transition: { duration: 0.8, ease: EASE, delay: 0.1 } }}
          exit={{ scaleY: 1, transformOrigin: "bottom", transition: { duration: 0.4, ease: EASE } }}
        >
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0, transition: { duration: 0.45, delay: 0.26 } }}
            className="font-mono text-[11px] uppercase tracking-[0.5em] text-[#C5A059]"
          >
            {t.nav[labelKey]}
          </motion.span>
          <span className="absolute bottom-0 left-0 h-[2px] w-full bg-[#C5A059]/60" />
        </motion.div>
      </AnimatePresence>
      <AnimatePresence mode="wait">
        <motion.main
          id="main"
          tabIndex={-1}
          key={location.pathname}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.5, delay: 0.3 } }}
          exit={{ opacity: 0, transition: { duration: 0.42 } }}
        >
          {outlet}
        </motion.main>
      </AnimatePresence>
    </>
  );
};

/* ---------------- Back to top (always visible) ---------------- */
const BackToTop = () => {
  const { t } = useLang();
  return (
    <div className="fixed bottom-6 right-6 z-[55] sm:bottom-8 sm:right-8">
      <Magnetic strength={0.25}>
        <button
          type="button"
          data-testid="back-to-top-button"
          data-cursor="TOP"
          aria-label={t.common.backToTop}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="group flex h-12 w-12 items-center justify-center rounded-full border border-[#C5A059]/50 bg-[#0B0B0C]/70 text-[#C5A059] backdrop-blur transition-all duration-300 hover:border-[#C5A059] hover:bg-[#C5A059] hover:text-[#0B0B0C]"
        >
          <ArrowUp className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5" />
        </button>
      </Magnetic>
    </div>
  );
};

const LayoutInner = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useLang();
  useRedX();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="grain min-h-screen bg-[#0B0B0C] text-[#F9F8F6]">
      <a href="#main" className="skip-link" data-testid="skip-link">{t.common.skip}</a>
      <Seo />
      <Cursor />
      <ScrollProgress />
      <Header onMenu={() => setMenuOpen(true)} />
      <FullscreenMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
      <PageTransition />
      <Footer />
      <BackToTop />
      <ConsentBanner />
    </div>
  );
};

const Layout = () => {
  const { lang } = useParams();
  if (lang !== "de" && lang !== "en") return <Navigate to="/de" replace />;
  return (
    <LanguageProvider>
      <ConsentProvider>
        <LayoutInner />
      </ConsentProvider>
    </LanguageProvider>
  );
};

export default Layout;
